import { Api } from "@penn/web-common";
import { RetryAfterGate } from "@/utils/calculation-request-control";
import { unwrap } from "@penn/web-common";
import type { RegionCode } from "./filter";

const configureOptionsRetryAfterGate = new RetryAfterGate();

/** Explicit reset for hermetic tests; runtime callers should honor the gate. */
export function clearConfigureOptionsRetryAfterGate(): void {
  configureOptionsRetryAfterGate.clear();
}

// =============================================================================
// Configure-Options API client.
//
// Server: GET /v1/products/:productId/configure-options on svc-04-admin-config.
// Spec: docs/spec/configure-solution-machine-options.md.
//
// All money / qty fields serialise as strings on the wire (rule §D.12).
// Modal renders them with the existing `formatMoney` util; never `Number()`.
// =============================================================================

export type ConfigureFamily =
  | "heger_pem"
  | "t_series"
  | "profil_indi"
  | "mds"
  | "sherex";

export type ConfigureOptionKind = "catalog" | "tooling" | "custom";

export type ConfigureOptionsUnavailableReason =
  | "no_cost_model"
  | "asia_pricing_unavailable"
  | "machine_options_unavailable"
  | "cost_model_unavailable";

export interface ConfigureOptionProperty {
  key: string;
  label: string;
  value: string | null;
}

export interface ConfigureOptionDto {
  partNo: string;
  itemName: string | null;
  description: string | null;
  category: string | null;
  subCategory: string | null;
  /** Decimal-as-string per rule §D.12. NULL = price not available. */
  price: string | null;
  kind: ConfigureOptionKind;
  /** Sherex-only structured properties; absent for other families. */
  properties?: ConfigureOptionProperty[];
  /** Profil-only — element type the row is priced for. */
  elementType?: string | null;
  /** Sherex-only — option thread variants for auto-select thread matching. */
  sherexThreadVariants?: string[];
}

export interface ToolingGroupDto {
  fastenerPartNo: string;
  fastenerLabel: string;
  items: ConfigureOptionDto[];
}

export interface MdsFiltersDto {
  fastenerGroup: string | null;
  feederTracks: string | null;
  forceKn: string | null;
  allowed: {
    fastenerGroup: string[];
    feederTracks: string[];
    forceKn: string[];
  };
}

export interface ConfigureOptionsLabels {
  title: string;
  subtitle: string;
  optionsHint: string;
  emptyMessage: string;
  /**
   * Copy for a machine no tooling is mapped to. Presence is not an instruction
   * to render it: the notice explains an otherwise-empty body, so the modal
   * shows it only when `catalogGroups` is empty.
   */
  toolingUnavailableMessage?: string;
}

export interface ConfigureOptionsMeta {
  configureFamily: ConfigureFamily;
  productId: string;
  productName: string | null;
  region: RegionCode;
  currencyCode: string;
  labels: ConfigureOptionsLabels;
  mdsFilters?: MdsFiltersDto;
  profilElementTypes?: string[];
  profilElementTypeDefault?: string | null;
  /**
   * Profil InDie auto-select signals (POC `applyProfilIndiAutoSelections`),
   * inferred server-side from the apps' PROFIL fasteners + profil_fasteners
   * master: `elementType`, `laneCount` (>=1), `fastenerType` (NUT/STUD or null).
   * The FE scales the catalog lines for `elementType` by `laneCount`. Present
   * only for `profil_indi` and only when an app-driven default exists.
   */
  profilSelection?: {
    elementType: string;
    laneCount: number;
    fastenerType: "NUT" | "STUD" | null;
  };
  /**
   * T-Series auto-select signals (POC `applyTSeriesAutoSelections`):
   * `appCount` = scoped apps; `typeCount` = distinct brand+type with qty>0.
   * `feederCount` is derived FE-side as `clamp(typeCount, 1, 8)`. Present only
   * for the `t_series` family.
   */
  tSeriesCounts?: { appCount: number; typeCount: number };
  /**
   * Decimal-as-string (rule §D.12). cost_models.additional_investment — the
   * family adder folded into the configured sum (MDS A240 robot adder). NULL /
   * absent = no adder. Exposed by svc-04 configure-options (gaps doc §4).
   */
  additionalInvestment?: string | null;
  /**
   * Sherex auto-select target set (POC `applySherexAutoSelections`): union of
   * the scoped apps' SHEREX fastener thread variants. The FE picks every
   * catalog row whose `sherexThreadVariants` intersect this set. Sherex only.
   */
  sherexTargetThreadVariants?: string[];
}

export interface ConfigureOptionsCustomTooling {
  partNo: string;
  name: string;
  price: string | null;
}

export interface ConfigureOptionsResponse {
  available: boolean;
  reason?: ConfigureOptionsUnavailableReason;
  meta: ConfigureOptionsMeta;
  catalogItems: ConfigureOptionDto[];
  toolingGroups?: ToolingGroupDto[];
  customTooling?: ConfigureOptionsCustomTooling;
}

export interface ConfigureOptionsQuery {
  region: RegionCode;
  applicationIds?: string[];
  mspAutoFeedEnabled?: boolean;
  /**
   * KPI / cost-path marker (POC-parity NA→EU price leak). The comparison-card
   * `machinePriceOverride` computation sends `costPath: true`; the
   * Configure-Solution modal omits it. When the server's
   * `CONFIGURE_PARITY_NA_PRICE_FROM_EU` flag is on, the NA→EU catalog leak
   * (heger_pem + t_series) applies ONLY to `costPath` requests, so the KPI cards
   * match POC's EU-leaked cost while the modal/quote stays on correct NA pricing.
   * Inert when the flag is off (server reads NA either way).
   */
  costPath?: boolean;
}

export async function getConfigureOptions(
  productId: string,
  query: ConfigureOptionsQuery,
  signal?: AbortSignal,
): Promise<ConfigureOptionsResponse> {
  const params: Record<string, string> = { region: query.region };
  if (query.applicationIds && query.applicationIds.length > 0) {
    // Server accepts both comma-separated string and repeated query params;
    // axios serialises arrays as repeated keys by default. We send a single
    // comma-joined string to keep the URL compact.
    params["applicationIds"] = query.applicationIds.join(",");
  }
  if (query.mspAutoFeedEnabled !== undefined) {
    params["mspAutoFeedEnabled"] = query.mspAutoFeedEnabled ? "true" : "false";
  }
  if (query.costPath) {
    params["costPath"] = "true";
  }
  return configureOptionsRetryAfterGate.run(async () => {
    const res = await Api.get(
      `/v1/products/${encodeURIComponent(productId)}/configure-options`,
      { params, signal },
    );
    return unwrap<ConfigureOptionsResponse>(res.data);
  });
}
